/**
 * Gating state machine for `AddTileDialog`'s "resolve an edited chart tile's
 * persisted column refs once the dialog's OWN preview lands" patch (P2
 * review finding companion — see `resolvedChartFieldsFromTile`).
 *
 * `AddTileDialog` is always mounted (rendered unconditionally by
 * `DashboardTab.tsx`, gated only by an `open` prop), so `useTilePreview`'s
 * state survives close/open across DIFFERENT tiles. On reopen, the open
 * effect calls `preview.reset()` then (for a query-backed edit) `preview.run()`
 * in the same synchronous callback — both are `setState` calls on the same
 * piece of state, so React batches them into a single commit and the
 * intermediate `status: 'idle'` is never actually rendered. The FIRST render
 * after reopening therefore still reflects the PREVIOUS session's
 * `status: 'done'` result (and its columns), landing on `'running'` only
 * once React flushes the batch.
 *
 * Resolving against whatever `columnInfos` happen to be non-empty on that
 * first render would silently resolve tile B's chart refs against tile A's
 * leftover columns. `useTilePreview`'s internal run-sequence guard means a
 * `status: 'done'` can only be produced by a run started during the CURRENT
 * session (a stale in-flight promise from a superseded run never calls
 * `setState`), so gating on "this session has observed its own `running`
 * status, and the NEXT `done` after that" is both necessary and sufficient
 * to know the columns belong to the tile being edited right now.
 */
import type { TilePreviewState } from './useTilePreview';

export type ChartRefsResolveSession = {
  /** Armed once this session has observed its own preview run start. */
  armed: boolean;
  /** Tile id already resolved this session — resolve fires at most once. */
  resolvedTileId: string | null;
};

export const INITIAL_CHART_REFS_RESOLVE_SESSION: ChartRefsResolveSession = {
  armed: false,
  resolvedTileId: null,
};

/**
 * Advances the session by one observation and says whether THIS observation
 * should trigger the resolve-and-patch. Pure — call once per render with the
 * current preview status, the tile being edited (`null` for a new tile), and
 * whether column info is available; store the returned `session` (e.g. in a
 * ref) and pass it back in on the next call.
 */
export function nextChartRefsResolveSession(
  session: ChartRefsResolveSession,
  input: { previewStatus: TilePreviewState['status']; tileId: string | null; hasColumns: boolean }
): { session: ChartRefsResolveSession; shouldResolve: boolean } {
  if (!input.tileId || session.resolvedTileId === input.tileId) {
    return { session, shouldResolve: false };
  }

  if (!session.armed) {
    if (input.previewStatus !== 'running') return { session, shouldResolve: false };
    return { session: { ...session, armed: true }, shouldResolve: false };
  }

  if (input.previewStatus === 'done' && input.hasColumns) {
    return { session: { ...session, resolvedTileId: input.tileId }, shouldResolve: true };
  }

  return { session, shouldResolve: false };
}
